import { type FormEvent, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { APP_NAME, ApiError, authApi } from "../../lib/api";
import { useAppPreferences } from "../../lib/preferences";

export function SetPasswordPage() {
  const { t } = useAppPreferences();
  const [searchParams] = useSearchParams();
  const tokenFromUrl = useMemo(() => searchParams.get("token")?.trim() ?? "", [searchParams]);
  const [token, setToken] = useState(tokenFromUrl);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setSaved(false);

    if (!token) {
      setError(t("This provider setup link is missing. Ask an administrator to send a new link."));
      return;
    }
    if (password.length < 8 || password.length > 128) {
      setError(t("Password must be between 8 and 128 characters."));
      return;
    }
    if (password !== confirmPassword) {
      setError(t("Passwords do not match."));
      return;
    }

    setSubmitting(true);
    try {
      await authApi.setPassword(token, password);
      setToken("");
      setPassword("");
      setConfirmPassword("");
      setSaved(true);
      window.history.replaceState({}, "", "/set-password");
    } catch (requestError) {
      const detail = requestError instanceof ApiError ? requestError.detail : "";
      setError(
        /expired|invalid/i.test(detail)
          ? t("This setup link is invalid or expired. Ask an administrator to send a new 7-day link.")
          : detail || t("Unable to set the password. Please try again."),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="shell-grid min-h-screen px-6 py-10 text-slate-100">
      <div className="mx-auto grid w-full max-w-3xl gap-6">
        <form className="glass-panel rounded-[32px] border border-white/10 p-8 sm:p-10" onSubmit={handleSubmit}>
          <p className="text-xs font-semibold uppercase tracking-[0.32em] text-sky-200">{APP_NAME} provider setup</p>
          <div className="mt-5 space-y-2">
            <h1 className="text-4xl font-semibold tracking-tight text-white">{t("Set your password")}</h1>
            <p className="text-sm leading-7 text-slate-300">
              {t("Choose a password to activate your account. This one-time link expires after 7 days.")}
            </p>
          </div>

          {!token && !saved ? (
            <div className="mt-8 rounded-[20px] border border-amber-400/20 bg-amber-400/10 px-4 py-3 text-sm leading-7 text-amber-100" role="alert">
              {t("This setup link is missing or has already been used. Ask an administrator to send a new link.")}
            </div>
          ) : null}

          {!saved && token ? (
            <div className="mt-8 grid gap-4">
              <label className="block">
                <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.24em] text-slate-400">{t("New password")}</span>
                <input autoComplete="new-password" className="ops-input w-full" maxLength={128} minLength={8} onChange={(event) => setPassword(event.target.value)} required type="password" value={password} />
              </label>
              <label className="block">
                <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.24em] text-slate-400">{t("Confirm password")}</span>
                <input autoComplete="new-password" className="ops-input w-full" maxLength={128} minLength={8} onChange={(event) => setConfirmPassword(event.target.value)} required type="password" value={confirmPassword} />
              </label>
            </div>
          ) : null}

          {saved ? (
            <div className="mt-8 rounded-[20px] border border-emerald-400/20 bg-emerald-400/10 px-4 py-4 text-sm leading-7 text-emerald-100" role="status">
              {t("Your password is set. Sign in to continue to your workspace.")}
            </div>
          ) : null}
          {error ? <div className="mt-4 rounded-[20px] border border-rose-400/20 bg-rose-400/10 px-4 py-3 text-sm text-rose-100" role="alert">{error}</div> : null}

          {!saved && token ? (
            <button className="ops-button mt-6 w-full" data-variant="primary" disabled={submitting} type="submit">
              {submitting ? t("Setting password...") : t("Set password")}
            </button>
          ) : null}

          <p className="mt-6 text-xs leading-6 text-slate-400">
            {saved ? null : <Link className="ops-action-link font-semibold text-sky-200" to="/forgot-password">{t("Need a different recovery link?")}</Link>}
            {saved ? <Link className="ops-action-link font-semibold text-sky-200" to="/sign-in">{t("Go to sign-in")}</Link> : null}
          </p>
        </form>
      </div>
    </div>
  );
}
